import { api } from '@/lib/api';
import type {
  DocumentKind,
  EquipmentAlias,
  EquipmentDocument,
  EquipmentModelDetail,
  EquipmentModelInput,
  EquipmentModelRow,
  EquipmentModelSensor,
  EquipmentPhoto,
  ModelStatus,
  ReviewPhotos,
  SubmissionDetail,
  SubmissionRow,
  SubmissionStatus,
} from './types';

const BASE = '/v4/equipment';

export const getEquipmentModels = (status: ModelStatus) =>
  api.get<{ models: EquipmentModelRow[] }>(`${BASE}/models`, { status }).then((r) => r.models);

export const getEquipmentModel = (id: string) => api.get<EquipmentModelDetail>(`${BASE}/models/${id}`);

export const createEquipmentModel = (input: EquipmentModelInput) =>
  api.post<{ model: EquipmentModelRow }>(`${BASE}/models`, input).then((r) => r.model);

export const updateEquipmentModel = (id: string, input: Partial<EquipmentModelInput>) =>
  api.put<{ model: EquipmentModelRow }>(`${BASE}/models/${id}`, input).then((r) => r.model);

export const archiveEquipmentModel = (id: string) =>
  api.post<{ model: EquipmentModelRow }>(`${BASE}/models/${id}/archive`, {}).then((r) => r.model);

export const restoreEquipmentModel = (id: string) =>
  api.post<{ model: EquipmentModelRow }>(`${BASE}/models/${id}/restore`, {}).then((r) => r.model);

export const addEquipmentAlias = (id: string, input: { brand: string; model: string }) =>
  api.post<{ alias: EquipmentAlias }>(`${BASE}/models/${id}/aliases`, input).then((r) => r.alias);

export const deleteEquipmentAlias = (id: string, aliasId: string) =>
  api.delete<void>(`${BASE}/models/${id}/aliases/${aliasId}`);

export const getEquipmentModelSensors = (id: string) =>
  api.get<{ sensors: EquipmentModelSensor[] }>(`${BASE}/models/${id}/sensors`).then((r) => r.sensors);

export function uploadEquipmentDocument(id: string, file: File, meta: { kind: DocumentKind; title: string; pageHint?: string }) {
  const formData = new FormData();
  formData.append('kind', meta.kind);
  formData.append('title', meta.title);
  if (meta.pageHint) formData.append('pageHint', meta.pageHint);
  formData.append('file', file);
  return api.upload<{ document: EquipmentDocument }>(`${BASE}/models/${id}/documents`, formData).then((r) => r.document);
}

export const linkEquipmentDocument = (id: string, input: { url: string; kind: DocumentKind; title: string }) =>
  api.post<{ document: EquipmentDocument }>(`${BASE}/models/${id}/documents/link`, input).then((r) => r.document);

export const deleteEquipmentDocument = (id: string, documentId: string) =>
  api.delete<void>(`${BASE}/models/${id}/documents/${documentId}`);

export function uploadEquipmentPhoto(id: string, file: File, meta: { kind: 'product' | 'install'; caption?: string }) {
  const formData = new FormData();
  formData.append('kind', meta.kind);
  if (meta.caption) formData.append('caption', meta.caption);
  formData.append('file', file);
  return api.upload<{ photo: EquipmentPhoto }>(`${BASE}/models/${id}/photos`, formData).then((r) => r.photo);
}

export const deleteEquipmentPhoto = (id: string, photoId: string) =>
  api.delete<void>(`${BASE}/models/${id}/photos/${photoId}`);

/** Short-lived signed URL (or the external link) for a document or photo. */
export const getEquipmentFileUrl = (kind: 'documents' | 'photos', fileId: string) =>
  api.get<{ url: string }>(`${BASE}/${kind}/${fileId}/url`).then((r) => r.url);

export const getSubmissions = (status: SubmissionStatus) =>
  api.get<{ submissions: SubmissionRow[] }>(`${BASE}/submissions`, { status }).then((r) => r.submissions);

export const getSubmission = (id: string) => api.get<SubmissionDetail>(`${BASE}/submissions/${id}`);

export const approveSubmission = (id: string, input: { model: EquipmentModelInput } & ReviewPhotos) =>
  api.post<{ submission: SubmissionRow; model: EquipmentModelRow }>(`${BASE}/submissions/${id}/approve`, input);

export const mergeSubmission = (id: string, input: { modelId: string } & ReviewPhotos) =>
  api.post<{ submission: SubmissionRow; model: EquipmentModelRow }>(`${BASE}/submissions/${id}/merge`, input);

export const rejectSubmission = (id: string, reason: string) =>
  api.post<{ submission: SubmissionRow }>(`${BASE}/submissions/${id}/reject`, { reason });
